import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { logger } from '../../../shared/utils/logger';
import { createPersistMiddleware, PERSIST_VERSION } from '../../../shared/stores/persistMigration';

const createEmptyOffsets = () => ({
  offsetX: 0,
  offsetY: 0,
  rotation: 0,
});

export interface FineTuningOffsets {
  offsetX: number;
  offsetY: number;
  rotation: number;
}

interface FineTuningDataState {
  modelFingerprint: string | null;
  modelName: string | null;
  offsets: FineTuningOffsets;
  updatedAt: string | null;
}

export interface IFCFineTuningState extends FineTuningDataState {
  registerModel: (fingerprint: string | null, modelName?: string | null) => void;
  saveAdjustments: (fingerprint: string, offsets: FineTuningOffsets, modelName?: string | null) => void;
  clearAdjustments: () => void;
  reset: () => void;
  hasDataFor: (fingerprint: string | null) => boolean;
  getOffsets: (fingerprint: string | null) => FineTuningOffsets | null;
}

const initialDataState: FineTuningDataState = {
  modelFingerprint: null,
  modelName: null,
  offsets: createEmptyOffsets(),
  updatedAt: null,
};

export const useIFCFineTuningStore = create<IFCFineTuningState>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialDataState,
        registerModel: (fingerprint, modelName) => {
          if (!fingerprint) {
            logger.log('IFC_FINE_TUNING_STORE', '🧹 Reset fine tuning (sin fingerprint)');
            set(() => ({
              ...initialDataState,
              offsets: createEmptyOffsets(),
            }));
            return;
          }

          set((state) => {
            if (state.modelFingerprint === fingerprint) {
              if (modelName && modelName !== state.modelName) {
                return { modelName };
              }
              return state;
            }

            logger.log('IFC_FINE_TUNING_STORE', `📁 Registrando ajustes para ${modelName || fingerprint}`);
            return {
              modelFingerprint: fingerprint,
              modelName: modelName ?? null,
              offsets: createEmptyOffsets(),
              updatedAt: null,
            };
          });
        },
        saveAdjustments: (fingerprint, offsets, modelName) => {
          if (!fingerprint) {
            logger.warn('IFC_FINE_TUNING_STORE', '⚠️ Intento de guardar ajustes sin fingerprint');
            return;
          }

          set(() => ({
            modelFingerprint: fingerprint,
            modelName: modelName ?? null,
            offsets: {
              offsetX: offsets.offsetX,
              offsetY: offsets.offsetY,
              rotation: offsets.rotation,
            },
            updatedAt: new Date().toISOString(),
          }));

          logger.log('IFC_FINE_TUNING_STORE', '💾 Ajustes finos guardados', offsets);
        },
        clearAdjustments: () => {
          set((state) => {
            if (!state.modelFingerprint) {
              return state;
            }
            logger.log('IFC_FINE_TUNING_STORE', '🧹 Ajustes finos reseteados');
            return {
              offsets: createEmptyOffsets(),
              updatedAt: null,
            };
          });
        },
        reset: () => {
          logger.log('IFC_FINE_TUNING_STORE', '🧹 Reset completo de ajustes finos');
          set(() => ({
            ...initialDataState,
            offsets: createEmptyOffsets(),
          }));
        },
        hasDataFor: (fingerprint) => {
          if (!fingerprint) return false;
          return get().modelFingerprint === fingerprint;
        },
        getOffsets: (fingerprint) => {
          if (!fingerprint) return null;
          const state = get();
          if (state.modelFingerprint !== fingerprint) {
            return null;
          }
          return {
            offsetX: state.offsets.offsetX,
            offsetY: state.offsets.offsetY,
            rotation: state.offsets.rotation,
          };
        },
      }),
      createPersistMiddleware('ifc-fine-tuning-store', { version: PERSIST_VERSION })
    ),
    {
      name: 'ifc-fine-tuning-store',
    }
  )
);

